import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { faClock, faPaperPlane } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUpRightFromSquare } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { getMinutes, isFuture, isPast } from "date-fns";
import type { LocationDescriptor } from "history";
import moment from "moment";
import useResizeObserver from "use-resize-observer";

import { ViewportContext } from "~/common";
import Attendees, { type AttendeeAttrs, type AttendeesPresenceInfo } from "~/common/Attendees";
import type { MeetingEvent } from "~/common/MeetingSelect";
import growthbook from "~/lib/ab/growthbook";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getAbsoluteDateString, getDateString, inVideocallMode } from "~/lib/utils";
// eslint-disable-next-line max-len
import { FLOATING_RECORDING_CONTROLS_FEATURE } from "~/meetingRecorder/RecordingControls/FloatingRecordingControls";
import { openShareMeetingModal } from "~/meetingRecorder/ShareMeetingModal";
import { MeetingInfoModalWrapper } from "~/nav/panels/Meetings/MeetingInfoModal";
import MeetingPhaseContext from "~/stream/MeetingPhaseContext";
import { Button, LinkButton, toast, Tooltip, useModals } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";

import { type NoteCardRouteState, updateNote } from "../NoteCard";
import NoteTimeSelectorModal from "../NoteTimeSelectorModal";
import RenameNoteModal from "../RenameNoteModal";
import JoinConferenceButton from "./JoinConferenceButton";
import MoreActionsMenu, { type MoreActionsMenuProps } from "./NoteActions/MoreActionsMenu";
import RecordingControls from "./RecordingControls/RecordingControls";
import RecordingErrorOrRestrictedIndicator from "./RecordingErrorOrRestrictedIndicator";

import type { NoteHeader_note$key } from "~/__generated__/NoteHeader_note.graphql";
import type { NoteHeader_viewer$key } from "~/__generated__/NoteHeader_viewer.graphql";
import type { NoteHeaderNoteDateOrTimer_note$key } from "~/__generated__/NoteHeaderNoteDateOrTimer_note.graphql";
import type { NoteHeaderTitleAndDate_note$key } from "~/__generated__/NoteHeaderTitleAndDate_note.graphql";

import noteCardStyles from "../css/NoteCard.module.css";
import styles from "./NoteHeader.module.css";

const MAX_TIMEOUT_DELAY = Math.pow(2, 31);

interface NoteDateProps {
	createdAt: Date | null;
	isCancelled: boolean;
	event: {
		start: Date;
		end: Date;
		isAllDay: boolean;
	} | null;
}

export function NoteDate({ createdAt, event, isCancelled }: NoteDateProps) {
	const startTime = (event && event.start) || createdAt;

	const dateString = useMemo(() => {
		if (!startTime) return "";
		return getDateString(startTime);
	}, [startTime]);

	const absoluteDateString = useMemo(() => {
		if (!startTime) return "";
		return getAbsoluteDateString(startTime);
	}, [startTime]);

	const timeString = useMemo(() => {
		if (!startTime) return "";

		if (!event?.end) {
			return startTime.toLocaleTimeString("en", {
				hour: "numeric",
				minute: getMinutes(startTime) === 0 ? undefined : "2-digit",
			});
		} else {
			const timeWithEndRemoved = event.start.toLocaleTimeString("en", {
				hour: "numeric",
				minute: getMinutes(event.start) === 0 ? undefined : "2-digit",
			});

			if (isPast(event.end) || inVideocallMode) return timeWithEndRemoved;

			const formatter = new Intl.DateTimeFormat("en", { hour: "numeric", minute: "2-digit", hour12: true });
			const timeRange = formatter
				.formatRangeToParts(event.start, event.end)
				.filter(({ type, source, value }) => {
					const isLiteralColon = type === "literal" && value === ":";
					if (type !== "minute" && !isLiteralColon) return true;

					// Hide the minutes value if it's :00
					switch (source) {
						case "startRange": {
							return getMinutes(event.start) !== 0;
						}
						case "endRange": {
							return getMinutes(event.end) !== 0;
						}
						default:
							return true;
					}
				})
				.map(({ type, value }) => (type === "dayPeriod" ? ` ${value}` : value));

			return (
				<>
					<span className={styles.hideIfSpace}>{timeWithEndRemoved}</span>
					<span className={styles.showIfSpace}>{timeRange}</span>
				</>
			);
		}
	}, [event?.end, event?.start, startTime]);

	if (!startTime) return <span>Unscheduled Note</span>;

	return (
		<span
			className={cx(styles.noteDate, {
				[styles.isFuture]: isFuture(event?.end || startTime) && !isCancelled,
				[styles.isCancelled]: isCancelled,
			})}
		>
			<b className={cx(styles.dateString, styles.hiddenWhenShortOnSpace)}>{dateString}</b>
			<b className={cx(styles.dateString, styles.visibleWhenShortOnSpace)}>{absoluteDateString}</b>
			{!event?.isAllDay && (
				<>
					<span className={cx(styles.circleSeparator, styles.isResponsive)} />
					<br />
					<span className={cx(styles.timeString, styles.isResponsive)}>{timeString}</span>
				</>
			)}
		</span>
	);
}

function NoteTitle({
	title,
	isExternal,
	inMeetingModal,
	noteUrl,
	onTitleClick,
}: {
	title: string;
	isExternal: boolean;
	inMeetingModal?: boolean;
	noteUrl?: string | null;
	onTitleClick?: () => void;
}) {
	const tooltipText = inMeetingModal ? "Open series" : "Rename note";
	const tooltipPlacement = inMeetingModal ? "top" : "bottom";

	// When setting navigation state on a `Link(Button)`, the `to` prop needs to be a LocationDescriptor.
	// We can't pass `noteUrl` as `descriptor.pathname` directly — if `noteURL` has a hash it won't be parsed correctly.
	// So we have to parse it manually and pass it along
	const noteUrlDescriptor = React.useMemo(() => {
		if (!noteUrl) return null;
		const noteUrlObj = new URL(window.location.origin.concat(noteUrl));

		return {
			state: {
				currentTab: "notes",
			},
			pathname: noteUrlObj.pathname,
			hash: noteUrlObj.hash,
			// I don't think `noteUrl` ever has query params, but I'm passing it along for good measure. -Wal
			search: noteUrlObj.search,
		} satisfies LocationDescriptor<NoteCardRouteState>;
	}, [noteUrl]);

	return (
		<span className={cx(styles.noteTitleContainer, { [styles.hideNoteTitleContainerIfNotInModal]: !inMeetingModal })}>
			<Tooltip
				content={tooltipText}
				placement={tooltipPlacement}
				disabled={isExternal || !onTitleClick}
				delay={[500, 0]}
			>
				<h2 className={styles.noteTitle}>
					{isExternal || !onTitleClick ? (
						<span className={styles.content}>{title}</span>
					) : inMeetingModal && noteUrlDescriptor ? (
						<LinkButton<NoteCardRouteState>
							to={noteUrlDescriptor}
							size="slim"
							variant="tertiary"
							className={cx(styles.ultraSlimButton, styles.content)}
							onClick={onTitleClick}
						>
							{title}
							<FontAwesomeIcon icon={faUpRightFromSquare} />
						</LinkButton>
					) : (
						<Button
							size="slim"
							variant="tertiary"
							className={cx(styles.ultraSlimButton, styles.content)}
							onClick={onTitleClick}
						>
							{title}
						</Button>
					)}
				</h2>
			</Tooltip>
		</span>
	);
}

function timerFormatter(mins: number) {
	let str = "";

	if (Math.abs(mins) >= 60) {
		const quotient = Math.floor(Math.abs(mins) / 60);
		const remainder = Math.abs(mins) % 60;

		if (remainder > 0) {
			str = `${str}${quotient}h${remainder}m`;
		} else {
			str = `${str}${quotient}h`;
		}
	} else {
		str = `${str}${Math.abs(mins)}m`;
	}

	return str;
}

interface NoteDateOrTimerProps {
	note: NoteHeaderNoteDateOrTimer_note$key;
	inMeetingModal?: boolean;
}

function NoteDateOrTimer({ note: noteKey, inMeetingModal }: NoteDateOrTimerProps) {
	const note = useFragment(
		graphql`
			fragment NoteHeaderNoteDateOrTimer_note on Note {
				id
				createdAt
				eventStart
				eventEnd
				noteType
				viewerCanEdit
				stream {
					isExternal
				}
				event {
					status
				}
				eventGuid
				...NoteTimeSelectorModal_note
			}
		`,
		noteKey,
	);

	const { createdAt, eventStart: noteStart, eventEnd: noteEnd } = note;

	const { isMobile } = useContext(ViewportContext);
	const { noMeeting, phase, getTimerValue, event: meetingEvent } = useContext(MeetingPhaseContext);

	const noteDateEvent = useMemo<NoteDateProps["event"]>(() => {
		if (noMeeting) {
			if (!noteStart || !noteEnd) return null;
			return {
				start: new Date(noteStart),
				end: new Date(noteEnd),
				isAllDay: false,
			};
		} else {
			return meetingEvent;
		}
	}, [noMeeting, noteStart, noteEnd, meetingEvent]);

	const [mins, setMins] = useState(() => Math.ceil(getTimerValue() / 60));

	useEffect(() => {
		let ticker: number;
		let tickerStarter: number;

		if (noMeeting) return;

		const startInterval = () => {
			if (noMeeting) return;

			const updateMins = () => {
				const seconds = getTimerValue();
				const minutes = Math.ceil(seconds / 60);

				setMins(prevMins => {
					if (minutes === prevMins) return prevMins;
					return minutes;
				});
			};

			updateMins();
			ticker = window.setInterval(updateMins, 1000);
		};

		const timeUntil15minBeforeInMilliseconds = (getTimerValue() - 15 * 60) * 1000;
		if (
			phase === "before" &&
			timeUntil15minBeforeInMilliseconds > 0 &&
			// Checking for upper bound of a 32-bit signed integer. This was ported from a previous implementation — I assume
			// 	it's either for number overflow or that `setTimeout` can only accept a delay so high. -Wal
			timeUntil15minBeforeInMilliseconds < MAX_TIMEOUT_DELAY
		) {
			tickerStarter = window.setTimeout(startInterval, timeUntil15minBeforeInMilliseconds);
		} else if (phase !== "after") {
			startInterval();
		}

		return () => {
			clearInterval(ticker);
			clearTimeout(tickerStarter);
		};
	}, [noMeeting, getTimerValue, phase]);

	const onChangeLinkedMeeting = React.useCallback(
		(event: MeetingEvent) => {
			const noteUpdates = {
				/* eslint-disable camelcase */
				event_guid: event.guid,
				event_title: event.title,
				event_start: event.start,
				event_end: event.end,
				/* eslint-enable camelcase */
			};

			// eslint-disable-next-line camelcase
			dispatchAnalyticsTrack("[meetings] Change associated meeting", { note_id: note.id });

			updateNote(note.id, noteUpdates, ({ updateNote }) => {
				if (updateNote && updateNote.ok) {
					if (inMeetingModal) {
						toast.success("Note is now linked.");
					} else {
						const oldDateTime = moment(note.eventStart || "");
						const scroll = oldDateTime.isValid() && oldDateTime.isBefore(event.start) ? "up" : "down";
						toast.success(`Note is now linked. You may need to scroll ${scroll} to find the note.`);
					}
				} else {
					toast.error("Note was not linked properly. Please sync your calendar and try again.");
				}
			});
		},
		[inMeetingModal, note.eventStart, note.id],
	);

	const modals = useModals();

	const openNoteTimeSelectorModal = useCallback(() => {
		if (!note.viewerCanEdit) return;
		return modals.open(({ close }) => (
			<NoteTimeSelectorModal closeModal={close} note={note} onChangeLinkedMeeting={onChangeLinkedMeeting} />
		));
	}, [modals, note, onChangeLinkedMeeting]);

	const openMeetingInfoModal = useCallback(() => {
		const {
			stream: { isExternal },
		} = note;

		const eventGuid = note.eventGuid; // For some reason we need to reassign this to a variable to make TS happy
		if (!eventGuid) return;
		modals.open(({ close }) => (
			<MeetingInfoModalWrapper
				eventGuid={eventGuid}
				onClose={close}
				note={note}
				noteType={note.noteType}
				isExternalUser={isExternal}
				onChangeLinkedMeeting={onChangeLinkedMeeting}
			/>
		));
	}, [modals, note, onChangeLinkedMeeting]);

	const isApproachingStart = phase === "before" && mins <= 15;
	const isApproachingEnd = phase === "started" && mins <= 5 && mins >= 0;
	const isOvertime = phase === "started" && mins < 0;

	if (!meetingEvent.isAllDay && !noMeeting && (phase === "started" || isApproachingStart)) {
		return (
			<Button
				variant="tertiary"
				size="slim"
				className={cx(styles.ultraSlimButton, styles.meetingTimer, {
					[styles.almostDone]: isApproachingEnd,
					[styles.overtime]: isOvertime,
				})}
				onClick={openMeetingInfoModal}
			>
				<FontAwesomeIcon icon={faClock} className={styles.icon} />
				&nbsp;
				{isApproachingStart && (
					<span className={cx({ [styles.hideIfNoSpace]: !inVideocallMode })}>Starts in&nbsp;</span>
				)}
				{timerFormatter(mins)}
				<span className={cx({ [styles.hideIfNoSpace]: !inVideocallMode })}>
					{phase === "started" && !isOvertime && <>&nbsp;left</>}
					{isOvertime && <>&nbsp;over</>}
				</span>
			</Button>
		);
	}

	const isCancelled = note.event?.status === "cancelled";
	const content = (
		<NoteDate createdAt={createdAt ? new Date(createdAt) : null} event={noteDateEvent} isCancelled={isCancelled} />
	);

	if (!note.viewerCanEdit) return content;

	if (note.stream.isExternal) {
		return (
			<Tooltip content="Meeting cancelled" placement="top" disabled={isMobile || !isCancelled} delay={[500, 0]}>
				<span>{content}</span>
			</Tooltip>
		);
	}

	if (note.eventGuid) {
		if (!note.event) {
			// Special case for note-only access, without event
			return <span className={cx(styles.ultraSlimButton, styles.noteDateOrTimer)}>{content}</span>;
		}

		return (
			<Tooltip
				content={isCancelled ? "Meeting cancelled" : "View event info"}
				placement="top"
				disabled={isMobile}
				delay={[500, 0]}
			>
				<Button
					size="slim"
					variant="tertiary"
					className={cx(styles.ultraSlimButton, styles.noteDateOrTimer)}
					onClick={openMeetingInfoModal}
				>
					{content}
				</Button>
			</Tooltip>
		);
	}

	return (
		<Tooltip content="Change date and time" placement="top" disabled={isMobile} delay={[500, 0]}>
			<Button
				variant="tertiary"
				size="slim"
				className={cx(styles.ultraSlimButton, styles.noteDateOrTimer)}
				onClick={openNoteTimeSelectorModal}
			>
				{content}
			</Button>
		</Tooltip>
	);
}

interface TitleAndDateProps {
	note: NoteHeaderTitleAndDate_note$key;
	inMeetingModal?: boolean;
	closeMeetingModal?: () => void;
}

function TitleAndDate({ note: noteKey, inMeetingModal, closeMeetingModal }: TitleAndDateProps) {
	const note = useFragment(
		graphql`
			fragment NoteHeaderTitleAndDate_note on Note {
				id
				noteTitle: title
				viewerCanEdit
				url
				stream {
					displayName
					isExternal
				}
				noteType
				createdAt
				eventGuid
				eventStart
				eventEnd
				...NoteTimeSelectorModal_note
				...NoteHeaderNoteDateOrTimer_note
			}
		`,
		noteKey,
	);

	const modals = useModals();

	const openRenameNoteModal = useCallback(() => {
		modals.open(({ close }) => <RenameNoteModal id={note.id} initialTitle={note.noteTitle} onClose={close} />);
	}, [modals, note.id, note.noteTitle]);

	const onTitleClick = useMemo(
		() => (!note.viewerCanEdit ? undefined : inMeetingModal ? closeMeetingModal : openRenameNoteModal),
		[closeMeetingModal, inMeetingModal, note.viewerCanEdit, openRenameNoteModal],
	);

	const noteHasTitle = useMemo(() => Boolean(note.noteTitle && note.stream.displayName !== note.noteTitle), [note]);

	return (
		<>
			{(noteHasTitle || inMeetingModal) && (
				<NoteTitle
					title={note.noteTitle}
					isExternal={note.stream.isExternal}
					inMeetingModal={inMeetingModal}
					noteUrl={note.url}
					onTitleClick={onTitleClick}
				/>
			)}

			<NoteDateOrTimer note={note} inMeetingModal={inMeetingModal} />
		</>
	);
}

export interface NoteHeaderProps extends MoreActionsMenuProps {
	noteKey: NoteHeader_note$key;
	viewerKey: NoteHeader_viewer$key;

	// Top level actions (title, date)
	trackNoteHeaderHeight?: (height: number) => void;

	// Attendees
	attendees: readonly AttendeeAttrs[];
	anonymousAttendees: readonly AttendeeAttrs[];
	attendeesPresenceInfo: AttendeesPresenceInfo;
	inMeetingModal?: boolean;
	closeMeetingModal?: () => void;
}

export default function NoteHeader({
	noteKey,
	viewerKey,

	trackNoteHeaderHeight,

	attendees,
	anonymousAttendees,
	attendeesPresenceInfo,

	inMeetingModal,
	closeMeetingModal,
	...moreActionsProps
}: NoteHeaderProps) {
	const viewer = useFragment(
		graphql`
			fragment NoteHeader_viewer on Viewer {
				user {
					isGuest
				}
				...RecordingControls_viewer
				...JoinConferenceButton_viewer
				...MoreActionsMenuNote_viewer
			}
		`,
		viewerKey,
	);

	const note = useFragment(
		graphql`
			fragment NoteHeader_note on Note {
				id
				noteTitle: title
				noteType
				hasMeetingRecordings
				viewerCanEdit
				stream {
					id
					displayName
				}
				...NoteHeaderTitleAndDate_note
				...RecordingControls_note
				event {
					guid
					...JoinConferenceButton_event
				}
				...MoreActionsMenu_note
			}
		`,
		noteKey,
	);

	const sendNotesTextIsOn = useFeatureIsOn("send-notes-button-text");
	const [wideEnoughForSendNotesText, setWideEnoughForSendNotesText] = useState(window.innerWidth > 500);

	const { ref: headerRef } = useResizeObserver<HTMLElement>({
		box: "border-box",
		onResize: ({ height, width }) => {
			if (height !== undefined && trackNoteHeaderHeight) trackNoteHeaderHeight(height);

			// This could be moved to a container query
			if (width !== undefined) {
				setWideEnoughForSendNotesText(width > 500);
			}
		},
	});

	const onSendNotesClick = React.useCallback(() => {
		dispatchAnalyticsTrack("Modal Opened", {
			/* eslint-disable camelcase */
			modal_id: "send_note",
			note_id: note.id,
			stream_id: note.stream.id,
			note_type: note.noteType,
			/* eslint-enable camelcase */
		});

		openShareMeetingModal(note.id, "Send");
	}, [note.id, note.noteType, note.stream.id]);

	const onShareNotesClick = React.useCallback(() => {
		dispatchAnalyticsTrack("Modal Opened", {
			/* eslint-disable camelcase */
			modal_id: "share_note",
			note_id: note.id,
			stream_id: note.stream.id,
			note_type: note.noteType,
			/* eslint-enable camelcase */
		});

		openShareMeetingModal(note.id, "Share");
	}, [note.id, note.noteType, note.stream.id]);

	const openSharedWithModal = React.useCallback(() => {
		openShareMeetingModal(note.id, "Share");
	}, [note.id]);

	const isFloatingRecordingControlsEnabled = growthbook.isOn(FLOATING_RECORDING_CONTROLS_FEATURE);

	return (
		<div className={cx(styles.headerContainer, { [styles.inMeetingModal]: inMeetingModal })}>
			<header
				ref={headerRef}
				className={cx(styles.header, noteCardStyles.header, { [styles.inMeetingModal]: inMeetingModal })}
			>
				<div className={styles.leftSide}>
					{isFloatingRecordingControlsEnabled && <RecordingErrorOrRestrictedIndicator eventGuid={note.event?.guid} />}
					<TitleAndDate note={note} inMeetingModal={inMeetingModal} closeMeetingModal={closeMeetingModal} />
					{!inMeetingModal && (
						<>
							{!isFloatingRecordingControlsEnabled && <RecordingControls viewer={viewer} note={note} />}
							{note.event && <JoinConferenceButton viewer={viewer} event={note.event} />}
						</>
					)}
				</div>
				<div className={styles.rightSide}>
					<Attendees
						avatarSize="1.5em"
						attendees={attendees.concat(anonymousAttendees)}
						presenceInfo={attendeesPresenceInfo}
						maxLength={10}
						onOpenShareModal={viewer.user.isGuest || !note.viewerCanEdit ? undefined : onShareNotesClick}
						onOpenSharedWithModal={openSharedWithModal}
					/>
					<div className={styles.iconButtons}>
						{note.viewerCanEdit && (
							<>
								{sendNotesTextIsOn && wideEnoughForSendNotesText ? (
									<Button
										data-tour-tooltip-target="share-notes-button"
										className={styles.sendNotesTextButton}
										variant="tertiary"
										size="slim"
										onClick={onSendNotesClick}
										data-test-id="note-header--send-notes"
									>
										<FontAwesomeIcon icon={faPaperPlane} />
										&ensp;Send
									</Button>
								) : (
									<Tooltip content={`Send Notes${note.hasMeetingRecordings ? " and Recap" : ""}…`}>
										<IconButton
											data-tour-tooltip-target="share-notes-button"
											icon={faPaperPlane}
											transform="translateX(-7%)" // optically centering the icon in the hover circle
											onClick={onSendNotesClick}
											title={`Send Notes${note.hasMeetingRecordings ? " and Recap" : ""}…`}
											data-test-id="note-header--send-notes"
										/>
									</Tooltip>
								)}
							</>
						)}
						{!note.viewerCanEdit && !note.hasMeetingRecordings ? (
							<></>
						) : (
							<MoreActionsMenu {...moreActionsProps} note={note} viewer={viewer} inMeetingModal={inMeetingModal} />
						)}
					</div>
				</div>
			</header>
		</div>
	);
}
